import type {
  BakedBlockInstance,
  BakedBlockPlacement,
  BlockDefinitionMeta,
  BorderEdge,
  RegionBorders,
  Template,
  Zone,
} from '@tsagko/label-contracts'
import { useTranslation } from 'react-i18next'
import type { LaidOutZone } from '../canvas/layout'
import { BorderPicker } from '../canvas/BorderPicker'
import { ZoneDimensionsField } from '../canvas/ZoneDimensionsField'
import {
  CollapsedRail,
  PanelCollapseToggle,
  panelBodyId,
  usePanelCollapsed,
} from '../ui/CollapsibleSection'
import { useConfirm } from '../ui/ConfirmDialog'
import { AlignmentGrid } from './AlignmentGrid'
import { TemplateVariablesForm, type VariableGroup } from './TemplateVariablesForm'
import { VersionChip } from './VersionChip'
import { computeVersionStatus } from './staleness'

/** Panel id for the collapse state + its localStorage key (#189). */
const PANEL_ID = 'properties'

interface TemplatePropertyPanelProps {
  template: Template
  blockMetas: BlockDefinitionMeta[]
  selectedZone?: LaidOutZone
  selectedBorders?: RegionBorders
  onSetEdge?: (edge: keyof RegionBorders, value: BorderEdge | undefined) => void
  onZoneDimensionsChange?: (next: { widthMm: number; heightMm: number }) => void
  onPlacementChange?: (next: {
    hAlign: BakedBlockPlacement['hAlign']
    vAlign: BakedBlockPlacement['vAlign']
  }) => void
  onOpenSwapPopover?: (
    baked: BakedBlockInstance,
    path: string[],
    hostBox: { widthMm: number; heightMm: number },
  ) => void
  onEditValues?: (baked: BakedBlockInstance, path: string[]) => void
  onDetach?: (baked: BakedBlockInstance, path: string[]) => void
  variableGroups?: VariableGroup[]
  variablesLoading?: boolean
  onVariableCommit?: (group: VariableGroup, name: string, value: string) => void
  onRetryBlockDef?: (zoneId: string) => void
  /**
   * When true, the label dimensions are locked: the root-zone size editor
   * (whose commits would resize the template itself) renders read-only.
   */
  dimensionsLocked?: boolean
}

function findBakedPath(
  zone: Zone,
  targetId: string,
  acc: string[],
): string[] | undefined {
  if (
    zone.component?.type === 'baked-block-instance' &&
    zone.component.id === targetId
  ) {
    return acc
  }
  if (zone.children) {
    const left = findBakedPath(zone.children[0], targetId, [...acc, '0'])
    if (left) return left
    const right = findBakedPath(zone.children[1], targetId, [...acc, '1'])
    if (right) return right
  }
  return undefined
}

function NoSelectionContent({
  variableGroups,
  variablesLoading,
  onVariableCommit,
  onRetryBlockDef,
}: {
  variableGroups?: VariableGroup[]
  variablesLoading?: boolean
  onVariableCommit?: (group: VariableGroup, name: string, value: string) => void
  onRetryBlockDef?: (zoneId: string) => void
}) {
  const { t } = useTranslation()
  if (!variableGroups?.length) {
    return (
      <p className="p-4 text-xs text-stone-400">
        {t('templateDesigner.propertyPanel.noSelection')}
      </p>
    )
  }
  if (variablesLoading) {
    return (
      <p className="p-4 text-xs text-stone-400">
        {t('templateDesigner.propertyPanel.loadingVariables')}
      </p>
    )
  }
  return (
    <div className="flex-1 overflow-auto p-4">
      <TemplateVariablesForm
        groups={variableGroups}
        onCommit={onVariableCommit ?? (() => {})}
        onRetry={onRetryBlockDef}
      />
    </div>
  )
}

export function TemplatePropertyPanel({
  template,
  blockMetas,
  selectedZone,
  selectedBorders,
  onSetEdge,
  onZoneDimensionsChange,
  onPlacementChange,
  onOpenSwapPopover,
  onEditValues,
  onDetach,
  variableGroups,
  variablesLoading,
  onVariableCommit,
  onRetryBlockDef,
  dimensionsLocked,
}: TemplatePropertyPanelProps) {
  const confirm = useConfirm()
  const { t } = useTranslation()
  const { collapsed, toggle, toggleRef } = usePanelCollapsed(PANEL_ID)
  const title = t('templateDesigner.panels.properties')

  const baked =
    selectedZone?.component?.type === 'baked-block-instance'
      ? (selectedZone.component as BakedBlockInstance)
      : undefined

  // Collapsed (#189): the 288px aside becomes a 36px rail and the width goes
  // to the canvas column, which is the `flex-1` sibling in the same row.
  if (collapsed) {
    return (
      <CollapsedRail
        panelId={PANEL_ID}
        label={title}
        onExpand={toggle}
        side="right"
        toggleRef={toggleRef}
      />
    )
  }

  return (
    // `aria-controls` names the aside itself, not an inner body wrapper: a
    // collapse here swaps the WHOLE panel for the rail, and the panel has two
    // mutually exclusive body branches that would otherwise need a wrapper
    // purely to own the id.
    <aside
      id={panelBodyId(PANEL_ID)}
      data-testid="template-property-panel"
      className="flex w-72 shrink-0 flex-col border-l border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900"
    >
      <div className="flex items-center gap-1.5 border-b border-stone-200 px-4 py-3 dark:border-stone-800">
        <h3 className="text-sm font-semibold">{title}</h3>
        <PanelCollapseToggle
          className="ml-auto"
          collapsed={false}
          onToggle={toggle}
          panelLabel={title}
          controls={panelBodyId(PANEL_ID)}
          testId={`${PANEL_ID}-toggle`}
          expandedChevron="right"
          buttonRef={toggleRef}
        />
      </div>

      {!selectedZone && (
        <NoSelectionContent
          variableGroups={variableGroups}
          variablesLoading={variablesLoading}
          onVariableCommit={onVariableCommit}
          onRetryBlockDef={onRetryBlockDef}
        />
      )}

      {selectedZone && (
        <div className="flex-1 space-y-4 overflow-auto p-4">
          {baked
            ? (() => {
                const meta = blockMetas.find(m => m.id === baked.blockId)
                const status = computeVersionStatus(
                  baked.blockVersion,
                  meta?.latestVersion,
                  meta?.hasDraft ?? false,
                )
                const path = findBakedPath(template.rootZone, baked.id, []) ?? []
                return (
                  <>
                    <section>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                        {t('templateDesigner.propertyPanel.regionSize')}
                      </p>
                      <ZoneDimensionsField
                        widthMm={selectedZone.w}
                        heightMm={selectedZone.h}
                        // Root-zone size edits resize the label itself —
                        // render read-only while the dimension lock is on.
                        disabled={dimensionsLocked === true && path.length === 0}
                        onChange={onZoneDimensionsChange}
                      />
                    </section>

                    <section>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                        {t('templateDesigner.propertyPanel.block')}
                      </p>
                      <div className="mt-1 flex items-center gap-2">
                        <span className="text-sm">
                          {meta?.name ?? baked.blockId} · v{baked.blockVersion}
                        </span>
                        <VersionChip
                          status={status}
                          bakedVersion={baked.blockVersion}
                          latestVersion={meta?.latestVersion}
                          hasDraft={meta?.hasDraft ?? false}
                          onClick={() => {
                            onOpenSwapPopover?.(baked, path, {
                              widthMm: selectedZone.w,
                              heightMm: selectedZone.h,
                            })
                          }}
                        />
                      </div>
                    </section>

                    <section>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
                        {t('templateDesigner.propertyPanel.placement')}
                      </p>
                      <div className="mt-1">
                        <AlignmentGrid
                          hAlign={baked.placement.hAlign}
                          vAlign={baked.placement.vAlign}
                          onChange={next => onPlacementChange?.(next)}
                          disabled={!onPlacementChange}
                        />
                      </div>
                    </section>

                    <section className="flex gap-2">
                      <button
                        type="button"
                        data-testid="tpp-edit-values"
                        onClick={() => onEditValues?.(baked, path)}
                        className="rounded border border-stone-300 px-3 py-1 text-xs hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-800"
                      >
                        {t('templateDesigner.propertyPanel.editValues')}
                      </button>
                      <button
                        type="button"
                        data-testid="tpp-detach"
                        onClick={() => {
                          void (async () => {
                            if (
                              !(await confirm({
                                title: t('confirm.detachBlock.title'),
                                danger: true,
                              }))
                            ) {
                              return
                            }
                            onDetach?.(baked, path)
                          })()
                        }}
                        className="rounded border border-stone-300 px-3 py-1 text-xs hover:bg-stone-50 dark:border-stone-700 dark:hover:bg-stone-800"
                      >
                        {t('common.detach')}
                      </button>
                    </section>
                  </>
                )
              })()
            : (
              <p className="text-xs text-stone-500">
                {t('templateDesigner.propertyPanel.dropBlockHere')}
              </p>
            )}

          <BorderPicker borders={selectedBorders} onSetEdge={onSetEdge ?? (() => {})} />
        </div>
      )}
    </aside>
  )
}
